import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map, Observable, startWith } from 'rxjs';
import { filter } from 'rxjs/operators';

import { APP_PATH, LEGAL_FRAGMENT, appUrl } from '@core/constants/app-paths';
import { MembershipApiService } from '@core/services/membership-api.service';
import { MembershipSessionService } from '@core/services/membership-session.service';
import { scrollToSection } from '@core/utils/scroll-to-section.util';
import { phoneHref } from '@lm-shared';
import { AsyncPipe } from '@angular/common';
import { AccountMenuComponent } from './account-menu.component';

/**
 * The four addresses a partner's own published page lives at.
 *
 * ⚠ THOSE PAGES CARRY THEIR OWN HEADER, so the site's must stand down on them. The owner's
 * finalised design (`index_self_contained.html`) opens with a Lifemaan wordmark, its own section nav
 * and a Share button; drawn inside the site chrome that is two Lifemaan headers stacked on one
 * screen, which is not what their reference shows.
 */
const STANDALONE_ROUTE_PREFIXES = ['/doctor/', '/hospital/', '/laboratory/', '/radio/'];

/**
 * The chrome: a header on a wide screen, a bottom bar on a phone.
 *
 * Both are the FRAME, so neither ever skeletons (CLAUDE.md: the frame is already
 * known; only the data area inside it waits). The bottom bar mirrors the patient-app
 * mock's three-item bar; its third item there is "Visits", which belongs to the
 * mobile app's existing consultations - on the web the third destination is the
 * member's own membership.
 */
@Component({
  selector: 'lm-app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss'],
  imports: [RouterLink, RouterLinkActive, AccountMenuComponent, RouterOutlet, AsyncPipe]
})
export class AppShellComponent {
  /** For the footer links to the /legal articles. */
  protected readonly legalFragment = LEGAL_FRAGMENT;
  /** Lifemaan's email and phone for the footer's "Contact us" (owner, 2026-10-27); null draws nothing. */
  protected readonly contact$ = inject(MembershipApiService).contactInfo();
  protected readonly phoneHref = phoneHref;
  readonly session = inject(MembershipSessionService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * True while a partner's own published page is on screen.
   *
   * ⚠ AN OBSERVABLE READ THROUGH `async`, NOT A FIELD SET IN A SUBSCRIPTION. This component is
   * `OnPush` and injects no `ChangeDetectorRef`, so a field written from a router subscription would
   * be correct in memory and never repaint — the header would stay on the page that asked it to go.
   *
   * ⚠ `startWith` MATTERS: `NavigationEnd` has already fired by the time this component subscribes
   * on a cold load, so without it the stream is silent and the chrome draws on the very case that
   * needs it hidden — somebody opening a partner's address directly, which is the whole point of
   * printing that address on a board.
   */
  readonly standalonePage$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event) => event.urlAfterRedirects),
    startWith(this.router.url),
    map((url) => STANDALONE_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix))),
  );

  /** "Get your LifeCard": the plan list, from anywhere — including from the plans page itself. */
  goToPlans(event?: Event): void {
    event?.preventDefault();
    // The plans page renders inside this shell's own <router-outlet>, so the section is looked for under this host (S17).
    scrollToSection(this.router, appUrl(APP_PATH.PLANS), 'plans', this.host.nativeElement).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
